/**
 * campaignAccess — pure access-control helpers for campaign templates.
 *
 * Ownership model (crm_campaign_templates):
 *   - ownership_type 'company' — org-wide template. Anyone can see/subscribe
 *     (subject to is_visible for non-admins); only real admins can edit.
 *   - ownership_type 'user'    — personal template. Only the creator can see
 *     and edit it. A real admin can open it ONLY while impersonating the
 *     creator via the "View as user" dropdown.
 *
 * `realAdmin` must be the caller's TRUE admin status (SuperAdmin or CRM Admin),
 * independent of impersonation — i.e. `canImpersonate` from ImpersonationContext,
 * NOT `effectiveIsAdmin` (which flips to the impersonated user's role).
 */

export function isPersonalTemplate(template) {
  return template?.ownershipType === 'user';
}

/**
 * Company Automation — an admin-only, backend-style campaign
 * (execution_scope='company') that fires on events from ANY owner's
 * deals/contacts through a single hidden runner subscription. Never shown
 * to non-admins, and its enrollments never appear on deal/contact panels.
 */
export function isCompanyAutomation(template) {
  return template?.executionScope === 'company';
}

/**
 * Can the caller edit/delete this template (builder save, delete buttons,
 * settings drawer edits)?
 */
export function canEditTemplate(template, { uid, realAdmin } = {}) {
  if (!template) return false;
  if (realAdmin) return true;
  return isPersonalTemplate(template) && !!uid && template.createdBy === uid;
}

/**
 * Can the caller open this template in the (editable) builder?
 *
 * Company templates: real admins only — non-admins use the read-only view.
 * Personal templates: the creator, or a real admin who is currently
 * impersonating the creator.
 */
export function canOpenTemplateInBuilder(template, { uid, realAdmin, impersonatedUid } = {}) {
  if (!template) return false;
  if (!isPersonalTemplate(template)) return !!realAdmin;
  if (!!uid && template.createdBy === uid) return true;
  return !!realAdmin && !!impersonatedUid && template.createdBy === impersonatedUid;
}

/**
 * Can the caller open the read-only campaign view for this template?
 * Company templates are viewable by everyone; personal templates follow the
 * same rule as the builder (creator, or admin impersonating the creator).
 * Company Automations are admin-only even in the read-only view.
 */
export function canViewTemplate(template, ctx = {}) {
  if (!template) return false;
  if (isCompanyAutomation(template)) return !!ctx.realAdmin;
  if (!isPersonalTemplate(template)) return true;
  return canOpenTemplateInBuilder(template, ctx);
}
